import { useRef } from "react"
import { StudyProtocolStatusEnum } from "@/generated/sdk"
import { useProtocolDetail, useUploadProtocol } from "@/hooks/queries/useProtocol"
import ProtocolApprovedView from "./ProtocolApprovedView"
import ProtocolEmptyState from "./ProtocolEmptyState"
import ProtocolExtractingState from "./ProtocolExtractingState"
import ProtocolFailedState from "./ProtocolFailedState"
import ProtocolPendingReview from "./ProtocolPendingReview"
import "./ProtocolPanel.css"

interface ProtocolPanelProps {
  studyId: string
}

/**
 * Main container for protocol UI within StudyDetails.
 *
 * Manages protocol state and renders the appropriate view based on status:
 *
 * - No protocol: ProtocolEmptyState
 * - NOT_STARTED/IN_PROGRESS: ProtocolExtractingState (with polling)
 * - FAILED: ProtocolFailedState
 * - PENDING_REVIEW: ProtocolPendingReview
 * - APPROVED: ProtocolApprovedView
 *
 * Handles file upload logic and passes callbacks to child components.
 */
const ProtocolPanel = ({ studyId }: ProtocolPanelProps) => {
  const { data: protocol, isPending, error } = useProtocolDetail(studyId)
  const uploadProtocol = useUploadProtocol(studyId)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const ext = file.name.split(".").pop()?.toLowerCase()
    if (ext !== "pdf") {
      return
    }

    const MAX_SIZE_MB = 50
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      return
    }

    try {
      await uploadProtocol.mutateAsync(file)
    } catch {
      // Error is surfaced to the UI via uploadProtocol.error (rendered by the child views).
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  const onUploadClick = () => fileInputRef.current?.click()

  const fileInput = (
    <input
      type="file"
      ref={fileInputRef}
      onChange={(e) => void handleUpload(e)}
      accept=".pdf"
      style={{ display: "none" }}
    />
  )

  if (isPending) {
    return (
      <div className="protocol-panel">
        <div className="study-skeleton-block tall" />
        <div className="study-skeleton-block short" />
        <div className="study-skeleton-block short" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="protocol-panel">
        <div className="protocol-error">
          <p>{error.message}</p>
        </div>
      </div>
    )
  }

  const uploading = uploadProtocol.isPending
  const uploadError = uploadProtocol.error?.message ?? null

  const renderContent = () => {
    if (!protocol) {
      return (
        <ProtocolEmptyState
          studyId={studyId}
          uploading={uploading}
          uploadError={uploadError}
          onUploadClick={onUploadClick}
        />
      )
    }

    switch (protocol.status) {
      case StudyProtocolStatusEnum.NotStarted:
      case StudyProtocolStatusEnum.InProgress:
        return <ProtocolExtractingState />
      case StudyProtocolStatusEnum.Failed:
        return (
          <ProtocolFailedState
            studyId={studyId}
            error={protocol.error}
            uploading={uploading}
            uploadError={uploadError}
            onUploadClick={onUploadClick}
          />
        )
      case StudyProtocolStatusEnum.PendingReview:
        return <ProtocolPendingReview studyId={studyId} data={protocol.data ?? null} />
      case StudyProtocolStatusEnum.Approved:
        return (
          <ProtocolApprovedView
            studyId={studyId}
            protocol={protocol}
            uploading={uploading}
            uploadError={uploadError}
            onUploadClick={onUploadClick}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="protocol-panel">
      {fileInput}
      {renderContent()}
    </div>
  )
}

export default ProtocolPanel
